import {moment, keyBy, groupBy, transform, overEvery, sortBy, createSelector} from 'vendor';
import {today, id, filter} from 'store/common';
import {pluralType} from './type';
import {status} from './status';
import {events} from 'store/google-calendar/selectors';

export const activities = createSelector(
  [ state => state.activities.index, events ],
  ( activities, events ) => ({ ...activities, ...events }),
);

export const activity = createSelector(
  [ activities, id ],
  ( activities, id ) => activities[id],
);

export const byType = createSelector(
  [ activities ],
  ( activities ) => groupBy(activities, pluralType),
);

export const onetime = createSelector(
  [ byType ],
  ( byType ) => byType.activities || [],
);

export const routines = createSelector(
  [ byType ],
  ( byType ) => byType.routines || [],
);

export const subroutines = createSelector(
  [ byType ],
  ( byType ) => byType.subroutines || [],
);

export const habits = createSelector(
  [ byType ],
  ( byType ) => byType.habits || [],
);

export const recurring = createSelector(
  [ routines, habits ],
  ( routines = [], habits = [] ) => [...routines, ...habits],
);

export const current = {
  recurring: createSelector(
    [recurring, today, filter],
    (recurring, today, filter) => recurring.filter(overEvery(
      filter,
      activity => /Completed|Not Started|Future/.test(status(activity, today.unix()))
    ))
  ),
  onetime: createSelector(
    [onetime, today, filter],
    (onetime, today, filter) => onetime.filter(overEvery(
      filter,
      activity => /Completed|Not Started|Future/.test(status(activity, today.unix()))
    ))
  )
};

current.byDate = createSelector(
  [ current.onetime ],
  ( onetime ) => groupBy(onetime, activity => activity.ts || 'someday'),
);

export const active = createSelector(
  [ activities ],
  ( activities ) => activities.filter(({scheduleDate, ts, status}) => scheduleDate || ts && status != 'On Hold')
);

export const withNumericalTarget = createSelector(
  [ recurring ],
  ( recurring ) => recurring.filter(({scheduleDate, numericalTarget}) => scheduleDate && numericalTarget)
);

export const byRepeatType = createSelector(
  [ recurring ],
  ( recurring ) => groupBy(recurring, 'repeatType'),
);

export const monthly = createSelector(
  [ byRepeatType ],
  ({ Monthly }) => keyBy(Monthly, 'id'),
);

export const weekly = createSelector(
  [ byRepeatType ],
  ({ PerWeek }) => keyBy(PerWeek, 'id'),
);

export const byGoalId = createSelector(
  [ activities ],
  ( activities ) => groupBy(activities, 'goal_id'),
);

export const byLocalMark = createSelector(
  [ activities ],
  ( activities ) => keyBy(activities, 'localMark'),
);

export const subroutinesById = createSelector(
  [ subroutines ],
  ( subroutines ) => groupBy(subroutines, 'dailyRoutineId'),
);

export const subtasksById = createSelector(
  state => state.activities.subs,
  subs => groupBy(subs, 'parentId'),
);

export const subtasks = createSelector(
  [ activity, subtasksById ],
  ( activity, subtasksById ) => sortBy(subtasksById[activity && activity.id], 'orderIndex'),
);

export const storedGoogleEvents = createSelector(
  [ state => state.activities.index ],
  ( activities ) => transform(activities, (result, activity) => {
    if (activity.googleEventId) {
      result['g_'+activity.googleEventId] = activity
    }
  }),
);

export const inProgress = {
  activities: createSelector([ onetime, today], filterInProgress),
  recurring: createSelector([ recurring, today], filterInProgress),
}

function filterInProgress(array = [], today) {
  return array.filter(activity => status(activity, today.unix()) == 'Not Started') || [];
}
